import {
  ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID,
  MSA_GEOGRAPHY_TAXONOMY_ID,
  SELECTION_LIST_IDS_BY_GEOGRAPHY_TAXONOMY_ID,
} from '../../../../shared/user-preferences/utils/geography-preference';
import type { TreeFilter, TreeSelection } from '../types/filters';

export const DEFAULT_PRIMARY_GEOGRAPHY: TreeFilter['selections'] = [
  { id: '1_1_2', name: 'New York City', levelId: 'msa' },
  { id: '5_4_1', name: 'London', levelId: 'msa' },
  { id: '3_8_3', name: 'Paris', levelId: 'msa' },
  { id: '1_1_21', name: 'San Francisco', levelId: 'msa' },
  { id: '4_5_5', name: 'Sao Paulo', levelId: 'msa' },
];

/**
 * Administrative-taxonomy counterpart to `DEFAULT_PRIMARY_GEOGRAPHY`. States,
 * not metros — the two taxonomies share no ids, so a metro default is not a
 * valid selection once the user switches.
 */
export const DEFAULT_PRIMARY_GEOGRAPHY_ADMINISTRATIVE: TreeFilter['selections'] =
  [
    { id: '1_1_2240', name: 'New York', levelId: 'state' },
    { id: '5_4_984', name: 'England', levelId: 'state' },
    { id: '3_8_1354', name: 'Ile-De-France', levelId: 'state' },
    { id: '1_1_572', name: 'California', levelId: 'state' },
    { id: '4_5_2997', name: 'Sao Paulo', levelId: 'state' },
  ];

/**
 * The default primary selections for a geography taxonomy.
 *
 * Geography's counterpart to `getRoleDefaultSelectionsForTaxonomy`, but
 * hardcoded rather than generated: there are only two taxonomies, and their
 * default entities are a product choice rather than something derivable from
 * the tree.
 *
 * A default from the wrong taxonomy is not merely unhelpful, it is invalid —
 * the two share no ids, so a metro default selects nothing once the user
 * switches to states.
 */
const DEFAULTS_BY_TAXONOMY_ID: Record<number, TreeFilter['selections']> = {
  [MSA_GEOGRAPHY_TAXONOMY_ID]: DEFAULT_PRIMARY_GEOGRAPHY,
  [ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID]:
    DEFAULT_PRIMARY_GEOGRAPHY_ADMINISTRATIVE,
};

export const getGeographyDefaultSelectionsForTaxonomy = (
  taxonomyId: number
): TreeFilter['selections'] =>
  DEFAULTS_BY_TAXONOMY_ID[taxonomyId] ?? DEFAULT_PRIMARY_GEOGRAPHY;

export const selectionsForGeographyTaxonomy = (
  selections: TreeFilter['selections'],
  taxonomyId: number
): TreeFilter['selections'] => {
  const levelIds = SELECTION_LIST_IDS_BY_GEOGRAPHY_TAXONOMY_ID[taxonomyId];
  if (!levelIds) return selections;
  const validLevelIds = new Set<string>(levelIds);
  return selections.filter((selection) => validLevelIds.has(selection.levelId));
};

const GEOGRAPHY_DEFAULT_SETS = [
  DEFAULT_PRIMARY_GEOGRAPHY,
  DEFAULT_PRIMARY_GEOGRAPHY_ADMINISTRATIVE,
];

// Defaults the pre-fix resolver seeded from the other taxonomy live on in URLs and saved views.
export const repairCrossTaxonomyGeographyDefaults = (
  selections: TreeSelection[],
  selectionListIds: readonly string[]
): TreeSelection[] => {
  const activeLevelIds = new Set(selectionListIds);
  const target = GEOGRAPHY_DEFAULT_SETS.find((set) =>
    set.every((selection) => activeLevelIds.has(selection.levelId))
  );
  if (!target) return selections;

  let repaired = false;
  const next = selections.map((selection) => {
    for (const set of GEOGRAPHY_DEFAULT_SETS) {
      if (set === target) continue;
      const index = set.findIndex(
        (candidate) =>
          candidate.id === selection.id && candidate.name === selection.name
      );
      const counterpart = target[index];
      if (index !== -1 && counterpart) {
        repaired = true;
        return { ...selection, ...counterpart };
      }
    }
    return selection;
  });
  if (!repaired) return selections;

  const seen = new Set<string>();
  return next.filter((selection) => {
    if (seen.has(selection.id)) return false;
    seen.add(selection.id);
    return true;
  });
};
